import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import {
  ArrowLeft,
  Camera,
  CheckCircle2,
  XCircle,
  MinusCircle,
  Flag,
  Clock,
  PlayCircle,
  Plus,
  Save,
  Wifi,
  WifiOff,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import { toast } from 'sonner';
import {
  useVisitDetail,
  useVisitChecklists,
  useVisitFindings,
  useStartVisit,
  useCompleteVisit,
  useUpdateChecklist,
  useCreateFinding,
  useUploadVisitPhoto,
  type ChecklistItem,
  type VisitChecklist,
} from '@/hooks/useVisits';

// ─── Draft Persistence ──────────────────────────────────────────────

interface DraftState {
  checklists: Record<string, ChecklistItem[]>;
  summary: string;
  customerNotes: string;
  internalNotes: string;
  savedAt: number;
}

function getDraftKey(visitId: string) {
  return `visit-draft-${visitId}`;
}

function loadDraft(visitId: string): DraftState | null {
  try {
    const raw = localStorage.getItem(getDraftKey(visitId));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function saveDraft(visitId: string, draft: DraftState) {
  try {
    localStorage.setItem(getDraftKey(visitId), JSON.stringify(draft));
  } catch { /* localStorage full — ignore */ }
}

function clearDraft(visitId: string) {
  localStorage.removeItem(getDraftKey(visitId));
}

// ─── Checklist Status Buttons ───────────────────────────────────────

const STATUS_OPTIONS: { value: ChecklistItem['status']; icon: typeof CheckCircle2; label: string; className: string }[] = [
  { value: 'pass', icon: CheckCircle2, label: 'Pass', className: 'text-green-500 hover:bg-green-500/10' },
  { value: 'fail', icon: XCircle, label: 'Fail', className: 'text-red-500 hover:bg-red-500/10' },
  { value: 'na', icon: MinusCircle, label: 'N/A', className: 'text-muted-foreground hover:bg-muted' },
  { value: 'flag', icon: Flag, label: 'Flag', className: 'text-amber-500 hover:bg-amber-500/10' },
];

function ChecklistItemRow({
  item,
  onChange,
  onPhoto,
}: {
  item: ChecklistItem;
  onChange: (updated: ChecklistItem) => void;
  onPhoto: (itemId: string) => void;
}) {
  return (
    <div className="space-y-2 py-2">
      <div className="flex items-center gap-1.5 flex-wrap">
        {STATUS_OPTIONS.map(opt => {
          const Icon = opt.icon;
          const active = item.status === opt.value;
          return (
            <Button
              key={opt.value}
              size="sm"
              variant={active ? 'default' : 'ghost'}
              className={cn('h-8 px-2 text-xs', !active && opt.className)}
              onClick={() => onChange({ ...item, status: opt.value })}
            >
              <Icon className="w-3.5 h-3.5 mr-1" />
              {opt.label}
            </Button>
          );
        })}
        <Button
          size="sm"
          variant="ghost"
          className="h-8 px-2 text-xs"
          onClick={() => onPhoto(item.id)}
        >
          <Camera className="w-3.5 h-3.5 mr-1" />
          Photo
        </Button>
      </div>
      <Textarea
        placeholder="Notes..."
        value={item.notes ?? ''}
        onChange={e => onChange({ ...item, notes: e.target.value || null })}
        rows={2}
        className="text-sm"
      />
      {item.photo_paths.length > 0 && (
        <p className="text-xs text-muted-foreground">{item.photo_paths.length} photo(s) attached</p>
      )}
    </div>
  );
}

// ─── Finding Form ───────────────────────────────────────────────────

function AddFindingDialog({
  open,
  onOpenChange,
  visitId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  visitId: string;
}) {
  const createFinding = useCreateFinding();
  const [category, setCategory] = useState('other');
  const [severity, setSeverity] = useState('medium');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [partsUsed, setPartsUsed] = useState('');
  const [recommendations, setRecommendations] = useState('');

  const handleSubmit = () => {
    if (!title.trim()) {
      toast.error('Title is required');
      return;
    }
    createFinding.mutate(
      {
        visitId,
        category,
        severity,
        title: title.trim(),
        description: description.trim() || undefined,
        partsUsed: partsUsed.trim() || undefined,
        recommendations: recommendations.trim() || undefined,
      },
      {
        onSuccess: () => {
          toast.success('Finding added');
          setTitle('');
          setDescription('');
          setPartsUsed('');
          setRecommendations('');
          onOpenChange(false);
        },
        onError: (err) => toast.error(`Failed: ${err instanceof Error ? err.message : 'Unknown'}`),
      }
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add Finding</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Category</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['connectivity','production','battery','generator','grid_utility','physical','configuration','warranty','other'].map(c => (
                    <SelectItem key={c} value={c}>{c.replace('_', ' ')}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Severity</Label>
              <Select value={severity} onValueChange={setSeverity}>
                <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['critical','high','medium','low','info'].map(s => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Title</Label>
            <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="Brief description" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Description</Label>
            <Textarea value={description} onChange={e => setDescription(e.target.value)} rows={2} />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Parts Used</Label>
            <Input value={partsUsed} onChange={e => setPartsUsed(e.target.value)} placeholder="e.g. 2x MC4 connectors" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Recommendations</Label>
            <Textarea value={recommendations} onChange={e => setRecommendations(e.target.value)} rows={2} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={createFinding.isPending}>Add Finding</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Main Component ─────────────────────────────────────────────────

export default function VisitDetail() {
  const { visitId } = useParams<{ visitId: string }>();
  const navigate = useNavigate();

  const { data: visit, isLoading: visitLoading } = useVisitDetail(visitId);
  const { data: checklists } = useVisitChecklists(visitId);
  const { data: findings } = useVisitFindings(visitId);
  const startVisit = useStartVisit();
  const completeVisit = useCompleteVisit();
  const updateChecklist = useUpdateChecklist();
  const uploadPhoto = useUploadVisitPhoto();

  const [localChecklists, setLocalChecklists] = useState<Record<string, ChecklistItem[]>>({});
  const [summary, setSummary] = useState('');
  const [customerNotes, setCustomerNotes] = useState('');
  const [internalNotes, setInternalNotes] = useState('');
  const [draftStatus, setDraftStatus] = useState<'saved' | 'synced' | 'dirty'>('synced');
  const [findingDialogOpen, setFindingDialogOpen] = useState(false);
  const photoInputRef = useRef<HTMLInputElement>(null);
  const [pendingPhotoItemId, setPendingPhotoItemId] = useState<string | null>(null);
  const [pendingPhotoChecklistId, setPendingPhotoChecklistId] = useState<string | null>(null);
  const draftTimerRef = useRef<ReturnType<typeof setTimeout>>();

  // Hydrate from localStorage or server
  useEffect(() => {
    if (!visitId || !checklists) return;
    const draft = loadDraft(visitId);
    if (draft) {
      setLocalChecklists(draft.checklists);
      setSummary(draft.summary);
      setCustomerNotes(draft.customerNotes);
      setInternalNotes(draft.internalNotes);
      setDraftStatus('saved');
    } else {
      const map: Record<string, ChecklistItem[]> = {};
      for (const cl of checklists) {
        map[cl.id] = cl.items;
      }
      setLocalChecklists(map);
    }
  }, [visitId, checklists]);

  useEffect(() => {
    if (visit) {
      if (!summary) setSummary(visit.summary ?? '');
      if (!customerNotes) setCustomerNotes(visit.customer_notes ?? '');
      if (!internalNotes) setInternalNotes(visit.internal_notes ?? '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- hydrate once
  }, [visit]);

  // Auto-save draft every 5 seconds when dirty
  const markDirty = useCallback(() => {
    setDraftStatus('dirty');
    if (draftTimerRef.current) clearTimeout(draftTimerRef.current);
    draftTimerRef.current = setTimeout(() => {
      if (!visitId) return;
      saveDraft(visitId, {
        checklists: localChecklists,
        summary,
        customerNotes,
        internalNotes,
        savedAt: Date.now(),
      });
      setDraftStatus('saved');
    }, 5000);
  }, [visitId, localChecklists, summary, customerNotes, internalNotes]);

  const handleChecklistItemChange = (checklistId: string, itemIndex: number, updated: ChecklistItem) => {
    setLocalChecklists(prev => {
      const items = [...(prev[checklistId] ?? [])];
      items[itemIndex] = updated;
      return { ...prev, [checklistId]: items };
    });
    markDirty();
  };

  const handleSaveChecklist = async (checklistId: string) => {
    const items = localChecklists[checklistId];
    if (!items || !visitId) return;
    updateChecklist.mutate(
      { checklistId, items, visitId },
      {
        onSuccess: () => {
          toast.success('Checklist saved');
          saveDraft(visitId, { checklists: localChecklists, summary, customerNotes, internalNotes, savedAt: Date.now() });
          setDraftStatus('synced');
        },
        onError: (err) => toast.error(`Save failed: ${err instanceof Error ? err.message : 'Unknown'}`),
      }
    );
  };

  const handleSaveNotes = async () => {
    if (!visitId) return;
    const { error } = await (await import('@/integrations/supabase/client')).supabase
      .from('service_visits')
      .update({ summary, internal_notes: internalNotes, customer_notes: customerNotes, updated_at: new Date().toISOString() })
      .eq('id', visitId);
    if (error) {
      toast.error('Failed to save notes');
    } else {
      toast.success('Notes saved');
      setDraftStatus('synced');
    }
  };

  const handlePhotoCapture = (checklistId: string, itemId: string) => {
    setPendingPhotoChecklistId(checklistId);
    setPendingPhotoItemId(itemId);
    photoInputRef.current?.click();
  };

  const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !visit || !visitId || !pendingPhotoChecklistId || !pendingPhotoItemId) return;
    try {
      const path = await uploadPhoto.mutateAsync({
        orgId: visit.organization_id,
        propertyId: visit.property_id,
        visitId,
        file,
      });
      // Update local checklist item with new photo path
      setLocalChecklists(prev => {
        const items = [...(prev[pendingPhotoChecklistId] ?? [])];
        const idx = items.findIndex(i => i.id === pendingPhotoItemId);
        if (idx >= 0) {
          items[idx] = { ...items[idx], photo_paths: [...items[idx].photo_paths, path] };
        }
        return { ...prev, [pendingPhotoChecklistId]: items };
      });
      markDirty();
      toast.success('Photo uploaded');
    } catch (err) {
      toast.error('Photo upload failed');
    }
    e.target.value = '';
  };

  const handleStart = () => {
    if (!visitId) return;
    startVisit.mutate(visitId, {
      onSuccess: () => toast.success('Visit started'),
      onError: (err) => toast.error(`Failed: ${err instanceof Error ? err.message : 'Unknown'}`),
    });
  };

  const handleComplete = () => {
    if (!visitId) return;
    completeVisit.mutate(
      { visitId, summary: summary.trim() || undefined, customerNotes: customerNotes.trim() || undefined },
      {
        onSuccess: () => {
          clearDraft(visitId);
          setDraftStatus('synced');
          toast.success('Visit completed');
        },
        onError: (err) => toast.error(`Failed: ${err instanceof Error ? err.message : 'Unknown'}`),
      }
    );
  };

  if (visitLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!visit) {
    return (
      <div className="text-center py-16">
        <p className="text-muted-foreground">Visit not found</p>
        <Button variant="link" onClick={() => navigate('/app/visits')}>Back to visits</Button>
      </div>
    );
  }

  const isEditable = visit.status === 'in_progress';

  return (
    <div className="space-y-4 pb-24">
      {/* Hidden file input for camera */}
      <input
        ref={photoInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={handleFileSelected}
      />

      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate('/app/visits')}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h1 className="text-lg font-semibold truncate">
              <Link to={`/app/property/${visit.property_id}`} className="hover:underline">
                {visit.property_id.slice(0, 8)}...
              </Link>
            </h1>
            <Badge variant="outline" className="text-[11px] capitalize">{visit.visit_type.replaceAll('_', ' ')}</Badge>
          </div>
          <div className="flex items-center gap-3 text-xs text-muted-foreground mt-0.5">
            {visit.scheduled_at && <span>Scheduled: {format(new Date(visit.scheduled_at), 'MMM d, h:mm a')}</span>}
            {visit.started_at && <span>Started: {format(new Date(visit.started_at), 'h:mm a')}</span>}
            {visit.completed_at && <span>Completed: {format(new Date(visit.completed_at), 'h:mm a')}</span>}
          </div>
        </div>
        {visit.status !== 'completed' && visit.status !== 'cancelled' && (
        <div className="shrink-0 flex items-center gap-2">
          {draftStatus === 'saved' && <span className="text-xs text-amber-500 flex items-center gap-1"><WifiOff className="w-3 h-3" />Draft</span>}
          {draftStatus === 'synced' && <span className="text-xs text-green-500 flex items-center gap-1"><Wifi className="w-3 h-3" />Synced</span>}
          {draftStatus === 'dirty' && <span className="text-xs text-muted-foreground">Saving...</span>}
        </div>
        )}
      </div>

      {/* Action buttons */}
      {visit.status === 'scheduled' && (
        <Button onClick={handleStart} disabled={startVisit.isPending} className="w-full">
          <PlayCircle className="w-4 h-4 mr-2" />
          {startVisit.isPending ? 'Starting...' : 'Start Visit'}
        </Button>
      )}
      {visit.status === 'in_progress' && (
        <Button onClick={handleComplete} disabled={completeVisit.isPending} variant="default" className="w-full">
          <CheckCircle2 className="w-4 h-4 mr-2" />
          {completeVisit.isPending ? 'Completing...' : 'Complete Visit'}
        </Button>
      )}

      {/* Tabs */}
      <Tabs defaultValue="checklists">
        <TabsList className="w-full grid grid-cols-4">
          <TabsTrigger value="checklists">Checklists</TabsTrigger>
          <TabsTrigger value="findings">Findings</TabsTrigger>
          <TabsTrigger value="notes">Notes</TabsTrigger>
          <TabsTrigger value="issues">Issues</TabsTrigger>
        </TabsList>

        {/* ─── Checklists Tab ─── */}
        <TabsContent value="checklists" className="space-y-4">
          {(checklists ?? []).length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">
              {visit.status === 'scheduled' ? 'Start the visit to generate checklists.' : 'No checklists.'}
            </p>
          ) : (
            (checklists ?? []).map((cl) => (
              <Card key={cl.id}>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm capitalize">{cl.template_type.replace(/_/g, ' ')}</CardTitle>
                    {isEditable && (
                      <Button size="sm" variant="outline" onClick={() => handleSaveChecklist(cl.id)}>
                        <Save className="w-3.5 h-3.5 mr-1" />
                        Save
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <Accordion type="multiple" className="w-full">
                    {(localChecklists[cl.id] ?? cl.items).map((item, idx) => (
                      <AccordionItem key={item.id} value={item.id}>
                        <AccordionTrigger className="text-sm py-2">
                          <div className="flex items-center gap-2">
                            {item.status === 'pass' && <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0" />}
                            {item.status === 'fail' && <XCircle className="w-4 h-4 text-red-500 shrink-0" />}
                            {item.status === 'flag' && <Flag className="w-4 h-4 text-amber-500 shrink-0" />}
                            {item.status === 'na' && <MinusCircle className="w-4 h-4 text-muted-foreground shrink-0" />}
                            {item.status === 'pending' && <Clock className="w-4 h-4 text-muted-foreground/50 shrink-0" />}
                            <span className="text-left">{item.label}</span>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent>
                          {isEditable ? (
                            <ChecklistItemRow
                              item={item}
                              onChange={(updated) => handleChecklistItemChange(cl.id, idx, updated)}
                              onPhoto={(itemId) => handlePhotoCapture(cl.id, itemId)}
                            />
                          ) : (
                            <div className="text-sm space-y-1">
                              <p>Status: <span className="capitalize">{item.status}</span></p>
                              {item.notes && <p className="text-muted-foreground">{item.notes}</p>}
                              {item.photo_paths.length > 0 && <p className="text-xs text-muted-foreground">{item.photo_paths.length} photo(s)</p>}
                            </div>
                          )}
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </CardContent>
              </Card>
            ))
          )}
        </TabsContent>

        {/* ─── Findings Tab ─── */}
        <TabsContent value="findings" className="space-y-4">
          {isEditable && (
            <Button size="sm" onClick={() => setFindingDialogOpen(true)}>
              <Plus className="w-4 h-4 mr-1" />
              Add Finding
            </Button>
          )}
          {(findings ?? []).length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">No findings recorded.</p>
          ) : (
            (findings ?? []).map(f => (
              <Card key={f.id}>
                <CardContent className="p-4 space-y-2">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-medium text-sm">{f.title}</p>
                      <p className="text-xs text-muted-foreground capitalize">{f.category.replace('_', ' ')}</p>
                    </div>
                    <Badge variant="outline" className={cn('text-[11px] capitalize',
                      f.severity === 'critical' && 'text-red-500 border-red-500/30',
                      f.severity === 'high' && 'text-orange-500 border-orange-500/30',
                      f.severity === 'medium' && 'text-amber-500 border-amber-500/30',
                    )}>
                      {f.severity}
                    </Badge>
                  </div>
                  {f.description && <p className="text-sm text-muted-foreground">{f.description}</p>}
                  {f.parts_used && <p className="text-xs text-muted-foreground">Parts: {f.parts_used}</p>}
                  {f.recommendations && <p className="text-xs text-muted-foreground">Rec: {f.recommendations}</p>}
                </CardContent>
              </Card>
            ))
          )}
        </TabsContent>

        {/* ─── Notes Tab ─── */}
        <TabsContent value="notes" className="space-y-4">
          <div className="space-y-2">
            <Label>Summary</Label>
            <Textarea
              value={summary}
              onChange={e => { setSummary(e.target.value); markDirty(); }}
              rows={3}
              disabled={!isEditable}
              placeholder="Overall visit summary..."
            />
          </div>
          <div className="space-y-2">
            <Label>Customer Notes</Label>
            <Textarea
              value={customerNotes}
              onChange={e => { setCustomerNotes(e.target.value); markDirty(); }}
              rows={3}
              disabled={!isEditable}
              placeholder="Notes for the customer report..."
            />
          </div>
          <div className="space-y-2">
            <Label>Internal Notes</Label>
            <Textarea
              value={internalNotes}
              onChange={e => { setInternalNotes(e.target.value); markDirty(); }}
              rows={3}
              disabled={!isEditable}
              placeholder="Internal-only notes..."
            />
          </div>
          {isEditable && (
            <Button onClick={handleSaveNotes} variant="outline">
              <Save className="w-4 h-4 mr-1" />
              Save Notes
            </Button>
          )}
        </TabsContent>

        {/* ─── Linked Issues Tab ─── */}
        <TabsContent value="issues">
          {visit.resolved_issue_ids.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">No linked issues.</p>
          ) : (
            <div className="space-y-2">
              {visit.resolved_issue_ids.map(issueId => (
                <Card key={issueId}>
                  <CardContent className="p-3">
                    <p className="text-sm font-mono">{issueId.slice(0, 8)}...</p>
                    <p className="text-xs text-muted-foreground">Will be resolved on visit completion</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <AddFindingDialog open={findingDialogOpen} onOpenChange={setFindingDialogOpen} visitId={visitId!} />
    </div>
  );
}
